// ADR 23.9.26w §4 — the queue's storage: database `lingara-offline-queue-v1`,
// one object store, `ops`, keyed `[userId, key]` with a `byUser` index.
//
// Its own database, apart from W2's `lingara-offline-v1` and the tutor's
// `lingara-tutor-v1`, because its lifetime is different: W2's cache is purged
// on sign-out and this store deliberately is not (§8). Hand-written in the
// tutor store's shape rather than a wrapper library, and **unguarded**: every
// failure rejects, and `queue.ts` is the one place that turns a rejection into
// "the feature is off" (10.9.26k's rule — storage refusal removes the feature,
// never the page).
//
// Upgrade path: a future v2 migrates records in `onupgradeneeded` and never
// drops the store. Nothing here is coupled to a cache format the way the
// native `flashcard_cache_v4` environment is.

import type { QueuedOp, StoredRecord } from './types'

const DB_NAME = 'lingara-offline-queue-v1'
const DB_VERSION = 1
const OPS = 'ops'
const BY_USER = 'byUser'

/** Everything the queue asks of storage. Suites run on `__tests__/memoryStore.ts`. */
export interface QueueStore {
  put(op: QueuedOp): Promise<void>
  get(userId: string, key: string): Promise<StoredRecord | undefined>
  list(userId: string): Promise<StoredRecord[]>
  /** Deletes only if the stored record still carries `rev` — a replaced record stays. */
  deleteIfRev(userId: string, key: string, rev: unknown): Promise<void>
  count(userId: string): Promise<number>
  /** Deletes every record, of any user, created before `cutoffMs`; resolves the deleted rows. */
  purgeOlderThan(cutoffMs: number): Promise<StoredRecord[]>
  /** ADR 30.9.26i §3 — deletes every record of `userId`: an erased account's ops. */
  purgeOwner(userId: string): Promise<void>
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB aborted'))
  })
}

/** Opens the database, reading `window.indexedDB` at call time (absent in jsdom). */
function openQueueDatabase(factory: () => IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const request = factory().open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        const ops = request.result.createObjectStore(OPS, { keyPath: ['userId', 'key'] })
        ops.createIndex(BY_USER, 'userId')
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
      request.onblocked = () => reject(new Error('IndexedDB open blocked'))
    } catch (error) {
      reject(error)
    }
  })
}

/** The IndexedDB adapter. `factory` is the test seam; the app reads `window.indexedDB`. */
export function createIdbQueueStore(factory: () => IDBFactory = () => window.indexedDB): QueueStore {
  async function run<T>(mode: IDBTransactionMode, work: (ops: IDBObjectStore) => Promise<T>): Promise<T> {
    const db = await openQueueDatabase(factory)
    try {
      const tx = db.transaction(OPS, mode)
      const done = transactionDone(tx)
      const result = await work(tx.objectStore(OPS))
      await done
      return result
    } finally {
      db.close()
    }
  }

  return {
    put: (op) => run('readwrite', async (ops) => void (await requestResult(ops.put(op)))),
    get: (userId, key) =>
      run('readonly', (ops) => requestResult(ops.get([userId, key])) as Promise<StoredRecord | undefined>),
    list: (userId) =>
      run('readonly', (ops) => requestResult(ops.index(BY_USER).getAll(userId)) as Promise<StoredRecord[]>),
    count: (userId) => run('readonly', (ops) => requestResult(ops.index(BY_USER).count(userId))),
    deleteIfRev: (userId, key, rev) =>
      run('readwrite', async (ops) => {
        const stored = (await requestResult(ops.get([userId, key]))) as StoredRecord | undefined
        if (stored !== undefined && stored.rev === rev) await requestResult(ops.delete([userId, key]))
      }),
    purgeOlderThan: (cutoffMs) =>
      run('readwrite', async (ops) => {
        const all = (await requestResult(ops.getAll())) as StoredRecord[]
        // A row with no readable `createdAt` is the drainer's `unreadable`, not an expiry.
        const expired = all.filter((row) => typeof row.createdAt === 'number' && row.createdAt < cutoffMs)
        await Promise.all(expired.map((row) => requestResult(ops.delete([row.userId, row.key]))))
        return expired
      }),
    purgeOwner: (userId) =>
      run('readwrite', async (ops) => {
        const keys = await requestResult(ops.index(BY_USER).getAllKeys(userId))
        await Promise.all(keys.map((key) => requestResult(ops.delete(key))))
      }),
  }
}
